import styled from 'styled-components/macro'
import { theme } from 'styled-tools'
import { media } from 'ui/rwd'
import { Section } from '../HomepageStyledComponents'

const BehindSection = styled(Section)`
  padding-top: 3.5rem;
  padding-bottom: 3.5rem;
  ${media.sm`
    padding-top: 4.5rem;
    padding-bottom: 4.5rem;
  `}
  ${media.md`
    padding-top: 5.5rem;
    padding-bottom: 5.5rem;
  `}
`

const BehindLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.25rem;
  align-items: center;
  ${media.md`
    grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
    gap: 3rem;
  `}
`

const BehindIntro = styled.div`
  margin-bottom: 2rem;
  text-align: left;
  ${media.sm`
    margin-bottom: 2.5rem;
  `}

  &:hover h2::after,
  &:focus-within h2::after {
    width: 4.5rem;
  }
`

const BehindKicker = styled.p`
  margin: 0 0 0.65rem;
  font-size: 0.8125rem;
  font-weight: bold;
  letter-spacing: 0.08em;
  line-height: 1.3;
  text-transform: uppercase;
  color: ${theme('pink')};
`

const BehindHeading = styled.h2`
  margin: 0;
  font-size: 1.75rem;
  font-weight: bold;
  line-height: 1.2;
  letter-spacing: -0.02em;
  ${media.sm`
    font-size: 2rem;
  `}

  &::after {
    content: '';
    display: block;
    width: 2.5rem;
    height: 3px;
    margin-top: 1rem;
    background-color: ${theme('yellow')};
    transition: width 0.3s ease;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      transition: none;
    }
  }
`

const BehindCopy = styled.div`
  min-width: 0;
  max-width: 42rem;
  text-align: left;
`

const BehindBody = styled.p`
  margin: 0;
  font-size: 1.0625rem;
  line-height: 1.7;
  color: ${theme('midGray')};
  ${media.sm`
    font-size: 1.125rem;
  `}

  & + & {
    margin-top: 1.25rem;
  }
`

const BehindName = styled.strong`
  font-weight: bold;
  color: ${theme('black')};
`

const BehindSlab = styled.span.attrs(props => ({
  'data-tone': props.$tone
}))`
  position: absolute;
  display: block;
  transition: transform 0.5s ease;
  background-color: ${props =>
    props.$tone === 'pink' ? theme('pink')(props) : theme('primary')(props)};

  ${props =>
    props.$tone === 'teal' &&
    `
    top: 0;
    right: 0;
    width: 58%;
    height: 42%;
  `}

  ${props =>
    props.$tone === 'pink' &&
    `
    bottom: 0;
    left: 0;
    width: 42%;
    height: 48%;
  `}
`

const BehindPanel = styled.div`
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  margin: 2.25rem 8% 1.75rem 18%;
  padding: 1.75rem 1.5rem 1.5rem;
  background-color: ${theme('yellow')};
  text-align: left;
  transform-origin: center center;
  transition: transform 0.5s ease;
  ${media.sm`
    margin: 2.5rem 6% 2rem 20%;
    padding: 2.25rem 2rem 1.85rem;
  `}
`

const BehindArt = styled.div`
  position: relative;
  min-height: 14.5rem;
  ${media.sm`
    min-height: 16.5rem;
  `}
  ${media.md`
    min-height: 20rem;
  `}

  &:hover ${BehindSlab}[data-tone='teal'] {
    transform: translate(5%, -8%);
  }

  &:hover ${BehindSlab}[data-tone='pink'] {
    transform: translate(-6%, 8%);
  }

  &:hover ${BehindPanel} {
    transform: scale(1.03);
  }

  @media (prefers-reduced-motion: reduce) {
    ${BehindSlab},
    ${BehindPanel} {
      transition: none;
    }

    &:hover ${BehindSlab},
    &:hover ${BehindPanel} {
      transform: none;
    }
  }
`

const BehindYear = styled.p`
  margin: 0;
  font-size: 3.25rem;
  font-weight: bold;
  line-height: 0.9;
  letter-spacing: -0.05em;
  color: ${theme('black')};
  ${media.sm`
    font-size: 4rem;
  `}
`

const BehindPlace = styled.p`
  margin: 0.85rem 0 0;
  font-size: 0.8125rem;
  font-weight: bold;
  letter-spacing: 0.08em;
  line-height: 1.3;
  text-transform: uppercase;
  color: ${theme('black')};
`

export {
  BehindSection,
  BehindLayout,
  BehindIntro,
  BehindKicker,
  BehindHeading,
  BehindCopy,
  BehindBody,
  BehindName,
  BehindArt,
  BehindSlab,
  BehindPanel,
  BehindYear,
  BehindPlace
}
